import { useTable } from "@/components/shared/data-table/useTable";
import { InvoiceType } from "@/lib/constants/invoiceStatus";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import type { SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { buildColumns } from "./columns";

export type ReceivablesTableInstance = ReturnType<typeof useReceivablesTable>;

export function useReceivablesTable({
  tableName,
  onView,
  onDownload,
  onDelete,
}: {
  tableName: string;
  onView: (invoiceId: string) => void;
  onDownload: (invoiceId: string, invoiceFileName: string) => void;
  onDelete: (invoiceId: string) => void;
}) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "createdAt", desc: true },
  ]);

  const {
    data: queryData,
    loading,
    error,
    pagination,
  } = useGetAllInvoices({
    query: {
      type: {
        value: InvoiceType.RECEIVABLE,
        op: "eq",
      },
    },
    page,
    limit,
    sort: sorting[0].id,
    order: sorting[0].desc ? "desc" : "asc",
  });

  const columns = useMemo(
    () =>
      buildColumns({
        onView,
        onDownload,
        onDelete,
      }),
    [onView, onDownload, onDelete],
  );

  const table = useTable({
    data: queryData?.map((invoice) => invoice.data) || [],
    columns,
    tableName,
    getRowId: (row) => row.invoiceDetailsId,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    sorting,
    onSortingChange: setSorting,
    stickyColumns: {
      left: ["select"],
      right: ["actions"],
    },
  });

  return {
    ...table,
    loading,
    error,
    pagination,
    setPage,
    setLimit,
  };
}
